import api from './api';
import { BaseService } from './base.service';
import {
  IssueHistoryFilters,
  IssueListFilters,
  IssueNotice,
  IssueNoticeAckRow,
  IssueNoticeAlert,
  IssueNoticeAuditRow,
  IssueNoticeCategory,
  IssueNoticeDetail,
  IssueNoticeFormPayload,
  IssueNoticeHistory,
  IssueNoticeList,
  IssueNoticeLive,
  IssueNoticePreview,
  IssueNoticePublicDetail,
  IssueNoticePublicLive,
  IssueNoticeUpdate,
  IssueSeverity,
  LocationGroup,
  SeverityConfigEntry,
  SeverityConfigMap,
} from '@/types/issue-notice';

function toQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) q.set(k, v.join(','));
    } else q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

class IssueNoticeService extends BaseService {
  constructor() {
    super('/issue-notices');
  }

  live() {
    return this.get<IssueNoticeLive>('/live');
  }

  /** No auth needed; used on the sign-in page. */
  async publicLive() {
    const res = await api.get<IssueNoticePublicLive>('/public/issue-notices/live');
    return res.data;
  }

  async publicDetail(id: string) {
    const res = await api.get<IssueNoticePublicDetail>(`/public/issue-notices/${id}`);
    return res.data;
  }

  list(status: 'active' | 'upcoming' | 'drafts', filters: IssueListFilters = {}) {
    return this.get<IssueNoticeList>(toQuery({ status, ...filters }));
  }

  history(filters: IssueHistoryFilters = {}) {
    return this.get<IssueNoticeHistory>(`/history${toQuery({ ...filters })}`);
  }

  historyAuthors() {
    return this.get<{ id: string; name: string }[]>('/history/authors');
  }

  async exportHistory(filters: IssueHistoryFilters = {}): Promise<Blob> {
    const res = await api.get(`/issue-notices/history/export${toQuery({ ...filters })}`, { responseType: 'blob' });
    return res.data as Blob;
  }

  detail(id: string) {
    return this.get<IssueNoticeDetail>(`/${id}`);
  }

  preview(payload: IssueNoticeFormPayload) {
    return this.post<IssueNoticePreview>('/preview', payload);
  }

  create(payload: IssueNoticeFormPayload) {
    return this.post<IssueNotice>('', payload);
  }

  update(id: string, payload: Partial<IssueNoticeFormPayload> & { version: number }) {
    return this.patch<IssueNotice>(`/${id}`, payload);
  }

  publish(id: string) {
    return this.post<IssueNotice>(`/${id}/publish`, {});
  }

  deleteDraft(id: string) {
    return this.delete<void>(`/${id}`);
  }

  duplicate(id: string) {
    return this.post<IssueNotice>(`/${id}/duplicate`, {});
  }

  postUpdate(id: string, body: string, notify: boolean) {
    return this.post<IssueNoticeUpdate>(`/${id}/updates`, { body, notify });
  }

  extend(id: string, payload: { extendByMinutes?: number; endsAt?: string }) {
    return this.post<IssueNotice>(`/${id}/extend`, payload);
  }

  resolve(id: string, resolutionNote: string) {
    return this.post<IssueNotice>(`/${id}/resolve`, { resolutionNote });
  }

  cancel(id: string) {
    return this.post<IssueNotice>(`/${id}/cancel`, {});
  }

  acknowledge(id: string) {
    return this.post<{ acknowledgedAt: string }>(`/${id}/ack`, {});
  }

  acks(id: string) {
    return this.get<{ acknowledged: IssueNoticeAckRow[]; notAcknowledged: IssueNoticeAckRow[] }>(`/${id}/acks`);
  }

  audit(id: string) {
    return this.get<IssueNoticeAuditRow[]>(`/${id}/audit`);
  }

  alerts(unreadOnly = false) {
    return this.get<{ unreadCount: number; data: IssueNoticeAlert[] }>(`/alerts${toQuery({ unreadOnly })}`);
  }

  markAlertsRead(ids?: string[]) {
    return this.post<{ unreadCount: number }>('/alerts/read', { ids });
  }

  categories(includeInactive = false) {
    return this.get<IssueNoticeCategory[]>(`/categories${toQuery({ includeInactive })}`);
  }

  createCategory(payload: { name: string; sortOrder?: number }) {
    return this.post<IssueNoticeCategory>('/categories', payload);
  }

  updateCategory(id: string, payload: Partial<Pick<IssueNoticeCategory, 'name' | 'sortOrder' | 'isActive'>>) {
    return this.patch<IssueNoticeCategory>(`/categories/${id}`, payload);
  }

  severityConfig() {
    return this.get<SeverityConfigMap>('/severity-config');
  }

  saveSeverityConfig(severity: IssueSeverity, entry: SeverityConfigEntry) {
    return this.put<SeverityConfigMap>(`/severity-config/${severity}`, entry);
  }
}

class LocationGroupService extends BaseService {
  constructor() {
    super('/location-groups');
  }

  list() {
    return this.get<LocationGroup[]>('');
  }

  create(payload: Omit<LocationGroup, 'id' | 'isActive'> & { isActive?: boolean }) {
    return this.post<LocationGroup>('', payload);
  }

  update(id: string, payload: Partial<Omit<LocationGroup, 'id'>>) {
    return this.patch<LocationGroup>(`/${id}`, payload);
  }

  remove(id: string) {
    return this.delete<void>(`/${id}`);
  }
}

export const issueNoticeService = new IssueNoticeService();
export const locationGroupService = new LocationGroupService();
